import type {
  EquipmentTypeRequest,
  EquipmentTypeResponse,
  SelectOption,
} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export const EquipmentTypeDocumentTitle = 'Equipment Types';

export const EquipmentTypeFormMeta = (
  inspectionTypes: SelectOption[]
): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    required: false,
    validation: {maxLength: 500},
  },
  {
    name: 'inspectionTypeIds',
    label: 'Inspection Types',
    type: 'select-multiple',
    options: inspectionTypes as ISelectOption[],
    required: false,
    description:
      'Tag this equipment type with the inspection types it should be covered by.',
  },
];

export const EquipmentTypeViewGeneralMeta = (
  inspectionTypes: SelectOption[]
): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    required: false,
    validation: {maxLength: 500},
  },
  {
    name: 'inspectionTypeIds',
    label: 'Inspection Types',
    type: 'select-multiple',
    options: inspectionTypes as ISelectOption[],
    required: false,
  },
];

/**
 * Resolves raw inspection type ids into `{id, label}` select options, the
 * shape `FormViewMultiSelectRowV2`/`FormSelectModalV2` need to render names
 * instead of bare ids. Unknown ids (deleted/inactive inspection types) are
 * dropped rather than surfaced as blank rows.
 */
export const ResolveInspectionTypeSelections = (
  inspectionTypeIds: number[] | undefined | null,
  inspectionTypes: SelectOption[]
): ISelectOption[] =>
  (inspectionTypeIds ?? []).flatMap(id => {
    const option = inspectionTypes.find(
      candidate => candidate.id === id
    );
    return option ? [{id: option.id, label: option.label ?? ''}] : [];
  });

// The API sends explicit `null` where the generated TS types say `undefined`.
export const MapEquipmentTypeResponseToEquipmentTypeRequest = (
  data: EquipmentTypeResponse
): EquipmentTypeRequest => {
  return {
    id: data.id,
    name: data.name,
    description: data.description ?? undefined,
    inspectionTypeIds: data.inspectionTypeIds ?? [],
  };
};
